import { all, takeEvery, put, call } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import Api from '../../../../helpers/api';
import { sendRequest } from '../../../../helpers/saga';
import {
  IMutationResult,
  IValidations,
  PRESERVE_STATUS,
  isMutationSuccess,
  mutationErrorMessage,
  mutationErrorNotification,
  mutationSuccessNotification,
  validationMessageAt,
} from '../../../../helpers/mutations';
import { logger } from '../../../../helpers/logger';
import { t } from '../../../../i18n';
import notifications from '../../../../redux/notifications/actions';
import actions from './actions';
import { ILegalEntityRecord, IStateOption, LegalEntityFieldErrors, LegalEntityFormField } from './types';
import {
  IBackendLegalEntity,
  ICreateAction,
  ILoadAction,
  IStatesApiResponse,
  IUpdateAction,
  LoadParallelResult,
} from './saga.types';
import { LEGAL_ENTITIES_PATH } from '../paths';

const legalEntitiesApi = new Api('legal_entities');
const statesApi = new Api('states/list?pageSize=99');

const FIELDS: LegalEntityFormField[] = ['name', 'tax_registration_number', 'street', 'city', 'state_id', 'zip'];

const FIELD_LABEL_KEYS: Record<LegalEntityFormField, string> = {
  name: 'dash.admin.legalEntities.form.name_label',
  tax_registration_number: 'dash.admin.legalEntities.form.tax_label',
  street: 'dash.admin.legalEntities.form.street_label',
  city: 'dash.admin.legalEntities.form.city_label',
  state_id: 'dash.admin.legalEntities.form.state_label',
  zip: 'dash.admin.legalEntities.form.zip_label',
};

function mapValidations(validations: IValidations): LegalEntityFieldErrors {
  const result: LegalEntityFieldErrors = {};

  FIELDS.forEach(field => {
    const message = validationMessageAt(validations, field);

    if (message) {
      result[field] = `${t(FIELD_LABEL_KEYS[field])} ${message}`;
    }
  });

  return result;
}

function successNotification(messageKey: string) {
  return mutationSuccessNotification(messageKey, 'dash.admin.legalEntities.legal_entity_title');
}

function errorNotificationFor(result: NonNullable<IMutationResult>, fieldErrors: LegalEntityFieldErrors) {
  const fieldError = FIELDS.map(field => fieldErrors[field]).find(Boolean);

  return mutationErrorNotification(mutationErrorMessage(result, fieldError));
}

function matchStateId(rawState: IBackendLegalEntity['state'], states: IStateOption[]): string {
  if (!rawState) return '';

  if (typeof rawState === 'object') {
    if (rawState.id) return rawState.id;
    const match = states.find(
      s => (rawState.code && s.code === rawState.code) || (rawState.name && s.name === rawState.name),
    );

    return match?.id ?? '';
  }

  const match = states.find(s => s.code === rawState || s.name === rawState);

  return match?.id ?? '';
}

function normalizeRecord(raw: IBackendLegalEntity, states: IStateOption[]): ILegalEntityRecord | null {
  if (!raw.id) return null;

  return {
    id: raw.id,
    name: raw.name ?? '',
    tax_registration_number: raw.tax_registration_number ?? null,
    street: raw.street ?? null,
    city: raw.city ?? null,
    state_id: raw.state_id ?? matchStateId(raw.state, states),
    zip: raw.zip ?? null,
  };
}

function* fetchStateOptions(): Generator<unknown, IStateOption[], IStatesApiResponse> {
  const result = yield call(sendRequest, statesApi.load, {}, notifications.REQUEST_ERROR);
  const records = result?.data?.data ?? [];

  return records.map(item => ({
    id: item.id,
    name: item.name ?? item.code ?? '',
    code: item.code,
  }));
}

function* loadStates(): Generator<unknown, void, IStateOption[]> {
  try {
    const options = yield call(fetchStateOptions);
    yield put(actions.loadStatesSuccess(options));
  } catch (error) {
    logger.error(error);
    yield put(actions.loadStatesSuccess([]));
  }
}

function* loadLegalEntity(action: ILoadAction): Generator<unknown, void, LoadParallelResult> {
  try {
    const [recordResult, stateOptions] = yield all([
      call(sendRequest, legalEntitiesApi.loadItem, action.payload.id, notifications.REQUEST_ERROR),
      call(fetchStateOptions),
    ]);

    yield put(actions.loadStatesSuccess(stateOptions));

    const raw = recordResult?.data?.success?.data?.legal_entity;
    const normalized = raw ? normalizeRecord(raw, stateOptions) : null;

    if (!normalized) {
      yield put(actions.loadError());

      return;
    }

    yield put(actions.loadSuccess(normalized));
  } catch (error) {
    logger.error(error);
    yield put(actions.loadError());
  }
}

function* updateLegalEntity(action: IUpdateAction): Generator<unknown, void, IMutationResult> {
  const { id, data } = action.payload;

  try {
    const result = yield call(
      sendRequest,
      legalEntitiesApi.updateItem,
      { data: { legal_entity: data }, id },
      notifications.REQUEST_ERROR,
      undefined,
      undefined,
      PRESERVE_STATUS,
    );

    if (isMutationSuccess(result)) {
      yield put(actions.updateSuccess());
      yield put(successNotification('dash.common.notification.updated_successfully'));
      yield put(push(LEGAL_ENTITIES_PATH));

      return;
    }

    const fieldErrors = mapValidations(result?.validations ?? {});

    yield put(actions.updateError({ fieldErrors }));

    if (result) {
      yield put(errorNotificationFor(result, fieldErrors));
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.updateError({ fieldErrors: {} }));
  }
}

function* createLegalEntity(action: ICreateAction): Generator<unknown, void, IMutationResult> {
  const { payload } = action;

  try {
    const result = yield call(
      sendRequest,
      legalEntitiesApi.createItem,
      { data: { legal_entity: payload } },
      notifications.REQUEST_ERROR,
      undefined,
      undefined,
      PRESERVE_STATUS,
    );

    if (isMutationSuccess(result)) {
      yield put(actions.createSuccess());
      yield put(successNotification('dash.common.notification.created_successfully'));
      yield put(push(LEGAL_ENTITIES_PATH));

      return;
    }

    const fieldErrors = mapValidations(result?.validations ?? {});

    yield put(actions.createError({ fieldErrors }));

    if (result) {
      yield put(errorNotificationFor(result, fieldErrors));
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.createError({ fieldErrors: {} }));
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD_STATES_REQUEST, loadStates),
    takeEvery(actions.CREATE_REQUEST, createLegalEntity),
    takeEvery(actions.LOAD_REQUEST, loadLegalEntity),
    takeEvery(actions.UPDATE_REQUEST, updateLegalEntity),
  ]);
}
